import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import AuthLayout from "../../../components/layout/AuthLayout";
import { loginAdmin, requestPasswordReset } from "../../../utils/auth";

const LOGIN_TITLE = "Willkommen zurück";
const LOGIN_SUBTITLE =
  "Melde dich mit deinem bestehenden AceTime-Zugang an. Lehrkräfte sehen danach nur ihre freigegebene Klasse, AceTime-Admins weiterhin alles.";

const PASSWORD_HELP_TEXT =
  "Noch kein Passwort? Nach der Freigabe deiner Anfrage bekommst du automatisch eine E-Mail zum Setzen deines Passworts.";

const PASSWORD_RESET_INFO =
  "Wir haben dir einen Link zum Passwortsetzen gesendet. Das klappt erst, sobald dein Zugang freigegeben und dein Login-Konto angelegt wurde.";

function EyeIcon({ open = false }) {
  return open ? (
    <svg viewBox="0 0 24 24" className="ac-auth-fieldicon" aria-hidden="true">
      <path
        d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" className="ac-auth-fieldicon" aria-hidden="true">
      <path
        d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="2.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M4.5 19.5 19.5 4.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ac-auth-fieldicon" aria-hidden="true">
      <path
        d="M4.5 7.5h15A1.5 1.5 0 0 1 21 9v6a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 15V9a1.5 1.5 0 0 1 1.5-1.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m4 8 8 6 8-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  function handleChange(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setInfo("");
    setLoading(true);

    const result = await loginAdmin({
      email: form.email,
      password: form.password,
    });

    setLoading(false);

    if (!result.ok) {
      setError(result.error || "Login fehlgeschlagen.");
      return;
    }

    navigate("/dashboard", { replace: true });
  }

  async function handleForgotPassword() {
    const email = form.email.trim();

    if (!email) {
      setError("Bitte zuerst deine E-Mail eingeben.");
      setInfo("");
      return;
    }

    setError("");
    setInfo("");

    const result = await requestPasswordReset(email);

    if (!result.ok) {
      setError(result.error || "Fehler beim Zurücksetzen.");
      return;
    }

    setInfo(PASSWORD_RESET_INFO);
  }

  function handleDemoAccess() {
    const demoUser = {
      uid: "demo-user",
      email: "demo@acetime.at",
      role: "admin",
      name: "Demo Admin",
      active: true,
      assignedClasses: ["5AD"],
    };

    localStorage.setItem("ac_admin_user", JSON.stringify(demoUser));
    navigate("/dashboard", { replace: true });
  }

  return (
    <AuthLayout title="AceTime Admin-Panel">
      <div className="ac-authcard-inner">
        <div className="ac-login-head">
          <div className="ac-login-badge">Sicherer Zugang</div>
          <h1 className="ac-login-title">{LOGIN_TITLE}</h1>
          <p className="ac-login-subtitle">{LOGIN_SUBTITLE}</p>
        </div>

        <div className="ac-login-success" style={{ marginBottom: 18 }}>
          <span className="ac-login-erroricon">i</span>
          <span>{PASSWORD_HELP_TEXT}</span>
        </div>

        {error && (
          <div className="ac-login-error">
            <span className="ac-login-erroricon">!</span>
            <span>{error}</span>
          </div>
        )}

        {info && (
          <div className="ac-login-success">
            <span className="ac-login-erroricon">✓</span>
            <span>{info}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="ac-login-form">
          <div className="ac-field">
            <label className="ac-label">E-Mail</label>
            <div className="ac-inputwrap">
              <input
                className="ac-input"
                type="email"
                value={form.email}
                onChange={(event) => handleChange("email", event.target.value)}
                placeholder="z. B. admin@acetime.at"
                autoComplete="email"
              />
              <span className="ac-inputicon" aria-hidden="true">
                <MailIcon />
              </span>
            </div>
          </div>

          <div className="ac-field">
            <label className="ac-label">Passwort</label>
            <div className="ac-inputwrap">
              <input
                className="ac-input ac-input-withbtn"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(event) => handleChange("password", event.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />

              <button
                type="button"
                className="ac-inputaction"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
          </div>

          <div className="ac-login-row ac-login-row-between">
            <button
              type="button"
              className="ac-login-linkbtn ac-login-linkbtn-subtle"
              onClick={handleForgotPassword}
            >
              Passwort vergessen?
            </button>
          </div>

          <button type="submit" className="ac-login-submit" disabled={loading}>
            {loading ? "Bitte kurz warten..." : "Anmelden"}
          </button>

          <button
            type="button"
            className="ac-login-secondary"
            onClick={handleDemoAccess}
            style={{ marginTop: "10px" }}
          >
            Demo-Zugang
          </button>
        </form>

        <div className="ac-login-divider">
          <span>Noch kein Zugang?</span>
        </div>

        <button
          type="button"
          className="ac-login-secondary"
          onClick={() => navigate("/request-access")}
        >
          Zugang anfordern
        </button>

        <div className="ac-login-meta">
          <span className="ac-login-meta-label">AceTime • Admin-Panel</span>
        </div>
      </div>
    </AuthLayout>
  );
}